import { useFormikContext } from "formik";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  listarDepartamentos,
  listarLocalidades,
  listarProvincias,
} from "../api/General";
import { useCatalog } from "../context/Catalog";
import { useTramite } from "../context/Tramite";
import { isInvalid } from "../utils/forms";
import "./css/components.css";
import { isEditable } from "../utils/tramite";
import Loader from "./controls/Loader";
import ObjectField from "./controls/ObjectField";
import Grid from "./layout/Grid";
import Row from "./layout/Row";
import Col from "./layout/Col";
import TextField from "./controls/TextField";
import OptionalTextField from "./controls/OptionalTextField";
import Label from "./controls/Label";
import SelectField from "./controls/SelectField";

export default function EmpresaForm({ ...props }) {
  const { tramite } = useTramite();
  const tramiteForm = useFormikContext();
  const { t } = useTranslation();
  const provinciaCodigo =
    tramiteForm.values.empresa?.domicilio?.provincia?.codigo;
  const departamentoCodigo =
    tramiteForm.values.empresa?.domicilio?.departamento?.codigo;
  const localidadCodigo =
    tramiteForm.values.empresa?.domicilio?.localidad?.codigo;
  const provinciasCatalog = useCatalog(
    ["provincias", "empresa"],
    () => listarProvincias(null),
  );
  const departamentosCatalog = useCatalog(
    provinciaCodigo ? ["departamentos", "empresa", provinciaCodigo] : null,
    () => listarDepartamentos({ provincia: provinciaCodigo }),
  );
  const localidadesCatalog = useCatalog(
    provinciaCodigo && departamentoCodigo
      ? ["localidades", "empresa", provinciaCodigo, departamentoCodigo]
      : null,
    () =>
      listarLocalidades({
        provincia: provinciaCodigo,
        departamento: departamentoCodigo,
      }),
  );
  const provincias = provinciasCatalog.data || [];
  const departamentos = provinciaCodigo ? departamentosCatalog.data || [] : [];
  const localidades =
    provinciaCodigo && departamentoCodigo ? localidadesCatalog.data || [] : [];
  const loading =
    provinciasCatalog.loading ||
    departamentosCatalog.loading ||
    localidadesCatalog.loading;
  const handleEmpresaChange = (event) => {
    tramiteForm.handleChange(event);
  };
  useEffect(() => {
    if (
      provinciaCodigo &&
      (!departamentoCodigo ||
        !departamentoCodigo.startsWith(provinciaCodigo))
    ) {
      tramiteForm.setFieldValue("empresa.domicilio.departamento", null, false);
      tramiteForm.setFieldValue("empresa.domicilio.localidad", null, false);
    } else if (!provinciaCodigo) {
      tramiteForm.setFieldValue(
        "empresa.domicilio.departamento",
        undefined,
        true,
      );
      tramiteForm.setFieldValue("empresa.domicilio.localidad", undefined, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provinciaCodigo]);
  useEffect(() => {
    if (!departamentoCodigo) {
      tramiteForm.setFieldValue("empresa.domicilio.localidad", null, false);
    } else if (
      localidadCodigo &&
      !localidadCodigo.startsWith(departamentoCodigo)
    ) {
      tramiteForm.setFieldValue("empresa.domicilio.localidad", null, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departamentoCodigo]);
  return (
    <Loader loading={loading && !tramite}>
      <Grid>
        <Row>
          <Col>
            <ObjectField
              id="empresa"
              name="empresa"
              value={tramiteForm.values.empresa}
              onChange={handleEmpresaChange}
            >
              {({ value: empresa, setProperty: setEmpresaProperty }) => (
                <Grid>
                  <Row>
                    <Col lg={12} md={3}>
                      <TextField
                        id="empresa_CUIT"
                        name="empresa.CUIT"
                        label={t("empresa.CUIT")}
                        value={empresa?.CUIT}
                        onChange={(event) => {
                          event.preventDefault();
                          setEmpresaProperty("CUIT", event.target.value);
                        }}
                        isInvalid={isInvalid("empresa.CUIT", tramiteForm)}
                        error={t(tramiteForm.errors.empresa?.CUIT)}
                        disabled={!isEditable(tramite)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={12}>
                      <TextField
                        id="empresa_razonSocial"
                        name="empresa.razonSocial"
                        label={t("empresa.razonSocial")}
                        value={empresa?.razonSocial}
                        onChange={(event) => {
                          event.preventDefault();
                          setEmpresaProperty("razonSocial", event.target.value);
                        }}
                        isInvalid={isInvalid(
                          "empresa.razonSocial",
                          tramiteForm,
                        )}
                        error={t(tramiteForm.errors.empresa?.razonSocial)}
                        disabled={!isEditable(tramite)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={12}>
                      <OptionalTextField
                        id="empresa_nombreFantasia"
                        name="empresa.nombreFantasia"
                        label={t("empresa.nombreFantasia")}
                        value={empresa?.nombreFantasia}
                        onChange={(event) => {
                          event.preventDefault();
                          setEmpresaProperty(
                            "nombreFantasia",
                            event.target.value,
                          );
                        }}
                        isInvalid={isInvalid(
                          "empresa.nombreFantasia",
                          tramiteForm,
                        )}
                        error={t(tramiteForm.errors.empresa?.nombreFantasia)}
                        disabled={!isEditable(tramite)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Label for="empresa_domicilio">
                        {t("empresa.domicilio")}
                      </Label>
                      <ObjectField
                        id="empresa_domicilio"
                        name="empresa.domicilio"
                        value={empresa?.domicilio}
                        onChange={(event) => {
                          event.preventDefault();
                          setEmpresaProperty("domicilio", event.target.value);
                        }}
                      >
                        {({
                          value: domicilio,
                          setProperty: setDomicilioProperty,
                        }) => (
                          <Grid>
                            <Row>
                              <Col lg={7}>
                                <TextField
                                  id="empresa_domicilio_calle"
                                  name="empresa.domicilio.calle"
                                  label={t("direccion.calle")}
                                  value={domicilio?.calle}
                                  onChange={(event) => {
                                    event.preventDefault();
                                    setDomicilioProperty(
                                      "calle",
                                      event.target.value,
                                    );
                                  }}
                                  isInvalid={isInvalid(
                                    "empresa.domicilio.calle",
                                    tramiteForm,
                                  )}
                                  error={t(
                                    tramiteForm.errors.empresa?.domicilio
                                      ?.calle,
                                  )}
                                  disabled={!isEditable(tramite)}
                                />
                              </Col>
                              <Col lg={2}>
                                <TextField
                                  id="empresa_domicilio_altura"
                                  name="empresa.domicilio.altura"
                                  label={t("direccion.altura")}
                                  value={domicilio?.altura}
                                  onChange={(event) => {
                                    event.preventDefault();
                                    setDomicilioProperty(
                                      "altura",
                                      event.target.value,
                                    );
                                  }}
                                  isInvalid={isInvalid(
                                    "empresa.domicilio.altura",
                                    tramiteForm,
                                  )}
                                  error={t(
                                    tramiteForm.errors.empresa?.domicilio
                                      ?.altura,
                                  )}
                                  disabled={!isEditable(tramite)}
                                />
                              </Col>
                              <Col lg={3}>
                                <OptionalTextField
                                  id="empresa_domicilio_unidadFuncional"
                                  name="empresa.domicilio.unidadFuncional"
                                  label={t("direccion.unidadFuncional")}
                                  value={domicilio?.unidadFuncional}
                                  onChange={(event) => {
                                    event.preventDefault();
                                    setDomicilioProperty(
                                      "unidadFuncional",
                                      event.target.value,
                                    );
                                  }}
                                  isInvalid={isInvalid(
                                    "empresa.domicilio.unidadFuncional",
                                    tramiteForm,
                                  )}
                                  error={t(
                                    tramiteForm.errors.empresa?.domicilio
                                      ?.unidadFuncional,
                                  )}
                                  disabled={!isEditable(tramite)}
                                />
                              </Col>
                            </Row>
                            <Row>
                              <Col lg={6}>
                                <SelectField
                                  id="empresa_domicilio_provincia"
                                  name="empresa.domicilio.provincia"
                                  label={t("direccion.provincia")}
                                  value={domicilio?.provincia}
                                  onChange={(event) => {
                                    event.preventDefault();
                                    setDomicilioProperty(
                                      "provincia",
                                      event.target.value,
                                    );
                                  }}
                                  options={provincias}
                                  valueAttr="codigo"
                                  labelAttr="nombre"
                                  emptyLabel="..."
                                  isInvalid={isInvalid(
                                    "empresa.domicilio.provincia",
                                    tramiteForm,
                                  )}
                                  error={t(
                                    tramiteForm.errors.empresa?.domicilio
                                      ?.provincia,
                                  )}
                                  disabled={!isEditable(tramite)}
                                />
                              </Col>
                              <Col lg={6}>
                                <SelectField
                                  id="empresa_domicilio_departamento"
                                  name="empresa.domicilio.departamento"
                                  label={t("direccion.departamento")}
                                  value={domicilio?.departamento}
                                  onChange={(event) => {
                                    event.preventDefault();
                                    setDomicilioProperty(
                                      "departamento",
                                      event.target.value,
                                    );
                                  }}
                                  options={departamentos}
                                  valueAttr="codigo"
                                  labelAttr="nombre"
                                  emptyLabel="..."
                                  isInvalid={isInvalid(
                                    "empresa.domicilio.departamento",
                                    tramiteForm,
                                  )}
                                  error={t(
                                    tramiteForm.errors.empresa?.domicilio
                                      ?.departamento,
                                  )}
                                  disabled={!isEditable(tramite)}
                                />
                              </Col>
                            </Row>
                            <Row>
                              <Col lg={10}>
                                <SelectField
                                  id="empresa_domicilio_localidad"
                                  name="empresa.domicilio.localidad"
                                  label={t("direccion.localidad")}
                                  value={domicilio?.localidad}
                                  onChange={(event) => {
                                    event.preventDefault();
                                    setDomicilioProperty(
                                      "localidad",
                                      event.target.value,
                                    );
                                  }}
                                  options={localidades}
                                  valueAttr="codigo"
                                  labelAttr="nombre"
                                  emptyLabel="..."
                                  isInvalid={isInvalid(
                                    "empresa.domicilio.localidad",
                                    tramiteForm,
                                  )}
                                  error={t(
                                    tramiteForm.errors.empresa?.domicilio
                                      ?.localidad,
                                  )}
                                  disabled={!isEditable(tramite)}
                                />
                              </Col>
                              <Col lg={2}>
                                <TextField
                                  id="empresa_domicilio_codigoPostal"
                                  name="empresa.domicilio.codigoPostal"
                                  label={t("direccion.codigoPostal")}
                                  value={domicilio?.codigoPostal}
                                  onChange={(event) => {
                                    event.preventDefault();
                                    setDomicilioProperty(
                                      "codigoPostal",
                                      event.target.value,
                                    );
                                  }}
                                  isInvalid={isInvalid(
                                    "empresa.domicilio.codigoPostal",
                                    tramiteForm,
                                  )}
                                  error={t(
                                    tramiteForm.errors.empresa?.domicilio
                                      ?.codigoPostal,
                                  )}
                                  disabled={!isEditable(tramite)}
                                />
                              </Col>
                            </Row>
                          </Grid>
                        )}
                      </ObjectField>
                    </Col>
                  </Row>
                </Grid>
              )}
            </ObjectField>
          </Col>
        </Row>
      </Grid>
    </Loader>
  );
}
